import { useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Printer, FileDown } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import DOMPurify from "dompurify";

function escapeHtml(str) {
  if (!str) return "\u2014";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const SHARED_STYLES = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  .header { text-align: center; border-bottom: 0.5mm solid #003399; padding-bottom: 1.5mm; margin-bottom: 1.5mm; }
  .gp-no { text-align: center; font-size: 13pt; font-weight: 900; margin: 1.5mm 0; letter-spacing: 1px; }
  .row { display: flex; font-size: 7.5pt; margin-bottom: 1mm; line-height: 1.3; }
  .label { font-weight: bold; min-width: 20mm; color: #555; font-size: 7pt; }
  .value { flex: 1; }
  .qr-row { display: flex; justify-content: center; margin: 2mm 0; }
  .footer { text-align: center; font-size: 5.5pt; color: #888; border-top: 0.3mm solid #ccc; padding-top: 1mm; margin-top: auto; position: absolute; bottom: 2mm; left: 3mm; right: 3mm; }
`;

function getPageStyles(isA4) {
  if (isA4) {
    return `@page { size: A4; margin: 10mm; }
      ${SHARED_STYLES}
      body { display: flex; align-items: center; justify-content: center; min-height: 100vh; font-family: Arial, sans-serif; }
      .card { width: 74mm; height: 105mm; border: 0.3mm dashed #ccc; padding: 2mm 3mm; position: relative; }`;
  }
  return `@page { size: 74mm 105mm; margin: 0; }
    ${SHARED_STYLES}
    body { width: 74mm; height: 105mm; font-family: Arial, sans-serif; padding: 2mm 3mm; position: relative; }`;
}

function buildBodyHtml(visitor, svgHtml, isA4) {
  const gpNo = escapeHtml(visitor?.gate_pass_no);
  const name = escapeHtml(visitor?.visitor_name);
  const company = escapeHtml(visitor?.company_from);
  const purpose = escapeHtml(visitor?.purpose);
  const meeting = escapeHtml(visitor?.whom_to_meet);
  const loc = escapeHtml(visitor?.location) + (visitor?.division ? " / " + escapeHtml(visitor.division) : "");
  const entryTime = visitor?.in_time
    ? escapeHtml(new Date(visitor.in_time).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: true }))
    : "\u2014";

  const content = `
    <div class="header">
      <div style="font-size:8pt;font-weight:900;letter-spacing:0.5px;color:#003399;">SINGHANIA INTERNATIONAL LIMITED</div>
      <div style="font-size:5pt;color:#666;margin-top:0.5mm;">C-25, Phase-1, Focal Point, Ludhiana - 141010</div>
    </div>
    <div class="gp-no">${gpNo}</div>
    <div class="row"><span class="label">Name:</span><span class="value"><b>${name}</b></span></div>
    <div class="row"><span class="label">Company:</span><span class="value">${company}</span></div>
    <div class="row"><span class="label">Purpose:</span><span class="value">${purpose}</span></div>
    <div class="row"><span class="label">Meeting:</span><span class="value">${meeting}</span></div>
    <div class="row"><span class="label">Location:</span><span class="value">${loc}</span></div>
    <div class="row"><span class="label">Entry:</span><span class="value">${entryTime}</span></div>
    <div class="qr-row">${svgHtml}</div>
    <div class="footer">Return this card at the gate upon exit &middot; ISO 9001:2015 | IATF 16949</div>
  `;

  return isA4 ? `<div class="card">${content}</div>` : content;
}

export default function GatePassLabel({ open, onClose, visitor }) {
  const printRef = useRef(null);

  const qrValue = visitor
    ? `${visitor.gate_pass_no || ""}|${visitor.in_time || ""}`
    : "";

  const handlePrint = useCallback((isA4) => {
    const dims = isA4 ? "width=800,height=1100" : "width=400,height=560";
    const printWindow = window.open("", "_blank", dims);
    if (!printWindow) return;
    const svgHtml = printRef.current?.querySelector("svg")?.outerHTML || "";

    // SECURITY: All user values are escaped via escapeHtml(), then the full HTML
    // is sanitized through DOMPurify.sanitize() before DOM insertion. SVG tags are
    // explicitly allowlisted for QR code rendering. Using createContextualFragment
    // avoids direct innerHTML assignment entirely.
    const pdoc = printWindow.document;
    pdoc.title = "Gate Pass";
    const styleEl = pdoc.createElement("style");
    styleEl.textContent = getPageStyles(isA4);
    pdoc.head.appendChild(styleEl);
    const sanitizedHtml = DOMPurify.sanitize(
      buildBodyHtml(visitor, svgHtml, isA4),
      { ADD_TAGS: ["svg", "path", "rect", "g"], ADD_ATTR: ["viewBox", "d", "fill", "xmlns", "width", "height", "x", "y", "transform"] }
    );
    const fragment = document.createRange().createContextualFragment(sanitizedHtml);
    pdoc.body.appendChild(fragment);
    setTimeout(() => printWindow.print(), 300);
  }, [visitor]);

  if (!visitor) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="gatepass-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Chivo" }}>Gate Pass — Print Preview</DialogTitle>
          <DialogDescription>Preview and print the visitor gate pass</DialogDescription>
        </DialogHeader>

        <div
          ref={printRef}
          className="mx-auto border border-gray-300 rounded p-2"
          style={{ width: "74mm", minHeight: "100mm", fontFamily: "Arial, sans-serif", position: "relative" }}
        >
          <div className="text-center border-b border-[#003399] pb-1.5 mb-1.5">
            <img src="/logo.png" alt="Logo" className="h-5 mx-auto" />
            <div className="text-[8pt] font-black tracking-wide text-[#003399]">SINGHANIA INTERNATIONAL LIMITED</div>
            <div className="text-[5pt] text-gray-500">C-25, Phase-1, Focal Point, Ludhiana - 141010</div>
          </div>
          <div className="text-center text-lg font-black tracking-wider my-1" data-testid="gp-number">
            {visitor.gate_pass_no || "\u2014"}
          </div>
          <div className="space-y-0.5 text-[7.5pt]">
            <div className="flex"><span className="font-bold w-[20mm] text-gray-500 text-[7pt]">Name:</span><span className="font-bold">{visitor.visitor_name}</span></div>
            <div className="flex"><span className="font-bold w-[20mm] text-gray-500 text-[7pt]">Company:</span><span>{visitor.company_from || "\u2014"}</span></div>
            <div className="flex"><span className="font-bold w-[20mm] text-gray-500 text-[7pt]">Purpose:</span><span>{visitor.purpose || "\u2014"}</span></div>
            <div className="flex"><span className="font-bold w-[20mm] text-gray-500 text-[7pt]">Meeting:</span><span>{visitor.whom_to_meet || "\u2014"}</span></div>
            <div className="flex"><span className="font-bold w-[20mm] text-gray-500 text-[7pt]">Location:</span><span>{visitor.location}{visitor.division ? ` / ${visitor.division}` : ""}</span></div>
            <div className="flex"><span className="font-bold w-[20mm] text-gray-500 text-[7pt]">Area:</span><span>{visitor.visit_area || "\u2014"}</span></div>
            <div className="flex"><span className="font-bold w-[20mm] text-gray-500 text-[7pt]">Entry:</span><span>{visitor.in_time ? new Date(visitor.in_time).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: true }) : "\u2014"}</span></div>
          </div>
          <div className="flex justify-center my-2">
            <QRCodeSVG value={qrValue} size={68} level="M" />
          </div>
          <div className="text-center text-[5.5pt] text-gray-400 border-t pt-1 absolute bottom-2 left-2 right-2">
            Return this card at the gate upon exit &middot; ISO 9001:2015 | IATF 16949
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => handlePrint(false)} className="bg-[#1B3C87]" data-testid="print-thermal-btn">
            <Printer size={16} className="mr-2" /> Print (Label Printer)
          </Button>
          <Button onClick={() => handlePrint(true)} variant="outline" data-testid="print-a4-btn">
            <FileDown size={16} className="mr-2" /> Print (A4 Fallback)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
